import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useColorMode } from '@chakra-ui/react';
import { Spinner, Center } from '@chakra-ui/react';

// 布局组件
import Layout from '@/components/Layout/Layout';

// 页面组件
import Dashboard from '@/pages/Dashboard';
import Recharge from '@/pages/Recharge';
import Invite from '@/pages/Invite';
import DORManagement from '@/pages/DORManagement';
import TransactionHistory from '@/pages/TransactionHistory';
import ReserveInfo from '@/pages/ReserveInfo';
import Settings from '@/pages/Settings';
import Help from '@/pages/Help';
import Whitepaper from '@/pages/Whitepaper';
import Welcome from '@/pages/Welcome';


// Hooks
import { useAppSelector } from '@/store';

// 设置管理
import { loadAllSettings, applyTheme } from '@/utils/settingsManager';
import i18n from '@/i18n';

// 全局组件
import PriceMonitor from '@/components/common/PriceMonitor';

// 加载组件
const LoadingSpinner = () => (
  <Center h="100vh">
    <Spinner size="xl" color="blue.500" />
  </Center>
);

const App: React.FC = () => {
  const { colorMode, setColorMode } = useColorMode();
  const { isConnected } = useAppSelector((state) => state.user);

  // 应用启动时加载设置
  useEffect(() => {
    try {
      const settings = loadAllSettings();
      
      // 应用语言设置
      i18n.changeLanguage(settings.language);
      
      // 应用主题设置
      const actualTheme = applyTheme(settings.theme);
      setColorMode(actualTheme);
      
      // Settings loaded
    } catch (error) {
              // Failed to load settings
    }
  }, [setColorMode]);

  return (
    <Suspense fallback={<LoadingSpinner />}>
      {/* 全局价格监控 */}
      <PriceMonitor />
      
      <Helmet>
        <title>DOR - Digital Open Reward Dollar | 目标锚定1 USDT | 数字开放奖励生态</title>
        <meta name="description" content="DOR是一个创新的数字开放奖励美元项目，目标锚定1 USDT，通过充值解锁双倍DOR和邀请激励，实现奖励驱动的hybrid系统。" />
        <meta name="keywords" content="DOR,数字开放奖励美元,去中心化,DeFi,USDT锚定,区块链,BSC,奖励机制,hybrid系统" />
        <meta name="author" content="DOR Team" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.svg" />
        
        {/* 主题色 */}
        <meta name="theme-color" content={colorMode === 'dark' ? '#1a202c' : '#0073E6'} />
        <meta name="msapplication-TileColor" content="#0073E6" />
        
        {/* Open Graph */}
        <meta property="og:title" content="DOR - Digital Open Reward Dollar" />
        <meta property="og:description" content="创新的数字开放奖励美元项目，目标锚定1 USDT，通过充值解锁双倍DOR和邀请激励实现奖励驱动hybrid系统" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.origin} />
        <meta property="og:image" content="/og-image.png" />
        <meta property="og:site_name" content="DOR Digital Open Reward Dollar" />
        <meta property="og:locale" content="zh_CN" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DOR - Digital Open Reward Dollar" />
        <meta name="twitter:description" content="创新的数字开放奖励美元项目，目标锚定1 USDT，通过充值解锁双倍DOR和邀请激励实现奖励驱动hybrid系统" />
        <meta name="twitter:image" content="/twitter-image.png" />
        <meta name="twitter:creator" content="@DORproject" />
        
        {/* 其他meta标签 */}
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <link rel="canonical" href={window.location.origin} />
      </Helmet>

      <Router>
        <Routes>
          {/* Welcome页面 - 独立页面，不包含Layout */}
          <Route path="/welcome" element={<Welcome />} />
          
          {/* 主应用路由 - 始终显示Layout */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="recharge" element={<Recharge />} />
            <Route path="invite" element={<Invite />} />
            <Route path="management" element={<DORManagement />} />
            <Route path="history" element={<TransactionHistory />} />
            <Route path="reserve" element={<ReserveInfo />} />
            <Route path="settings" element={<Settings />} />
            <Route path="help" element={<Help />} />
            <Route path="whitepaper" element={<Whitepaper />} />
            
          </Route>
        </Routes>
      </Router>
    </Suspense>
  );
};

export default App; 